export class SandboxView {
    constructor(config) {
        this.containerId = config.container;
        
        // --- FIX 1: DEEP CLONE (Solves "Vanishing" candidates in main view) ---
        // We create a completely isolated copy of the data. 
        // This ensures the Sandbox never touches the main OrganiGram data reference.
        try {
            this.data = config.data ? JSON.parse(JSON.stringify(config.data)) : [];
        } catch (e) {
            console.warn("Deep clone error, falling back", e);
            this.data = config.data ? [...config.data] : [];
        }   
        
        this.rootId = config.rootId;
        this.actions = config.actions || {};
        this.displaySettings = config.displaySettings || { cc: '#F59E0B', tc: '#fff', bc: '#e5e7eb' };
        this.onClose = config.onClose || (() => {});
        
        this.cardWidth = 340; 
        this.cardHeight = 220; 
        this.gapX = 80;
        this.gapY = 160;
        
        this.zoom = 0.6;
        this.panX = 0;
        this.panY = 0;
        this.isDragging = false;
        
        this.selectedNodeId = null;
        this.isNodeDragging = false;
        this.dragNode = null;
        this.dragStart = { x: 0, y: 0 };
        this.nodeStart = { x: 0, y: 0 };
        this.originalPosBeforeMove = {}; 
        
        this.handleKeys = this.handleKeys.bind(this);
        this.handleRefresh = this.handleRefresh.bind(this);
        
        this.init();
    }

    init() {
        document.body.style.overflow = 'hidden';
        this.injectStyles();
        this.renderLayout();
        
        window.addEventListener('popstate', () => this.destroy());
        window.addEventListener('keydown', this.handleKeys);
        window.addEventListener('kvdr-sandbox-refresh', this.handleRefresh);

        setTimeout(() => {
            this.refreshData();
            this.resetView(); 
        }, 50);
        
        this.attachEvents();
    }

    handleRefresh(e) {
        if (e.detail && e.detail.graphData) {
            try {
                this.data = JSON.parse(JSON.stringify(e.detail.graphData));
            } catch (err) {
                this.data = e.detail.graphData;
            }
        }
        this.refreshData();
    }

    handleKeys(e) { 
        if(e.key === "Escape") {
            if (this.isNodeDragging || this.selectedNodeId) this.cancelMove();
            else this.destroy(); 
            return;
        }
        if (this.selectedNodeId && !this.isNodeDragging) {
            const step = e.shiftKey ? 10 : 1; 
            let dx = 0; let dy = 0;
            if (e.key === "ArrowUp") dy = -step;
            if (e.key === "ArrowDown") dy = step;
            if (e.key === "ArrowLeft") dx = -step;
            if (e.key === "ArrowRight") dx = step;

            if (dx !== 0 || dy !== 0) {
                e.preventDefault();
                this.moveSelectedNodeBy(dx, dy);
            }
        }
    }

    destroy() {
        if (this.wrapper) this.wrapper.remove();
        document.body.style.overflow = '';
        window.removeEventListener('keydown', this.handleKeys);
        window.removeEventListener('kvdr-sandbox-refresh', this.handleRefresh);
        this.onClose();
    }

    refreshData() {
        this.calculateTreeLayout();
        this.draw();
        this.drawMinimap();
    }

    injectStyles() {
        const oldStyle = document.getElementById('sb-styles-ui-v2');
        if (oldStyle) oldStyle.remove();

        const css = `
            .sb-wrapper {
                position: fixed !important; top: 0; left: 0; width: 100vw; height: 100vh;
                z-index: 2147483647 !important; background-color: #f8f9fa;
                background-image: radial-gradient(#cbd5e1 1.5px, transparent 1.5px);
                background-size: 24px 24px; font-family: 'Inter', sans-serif;
                overflow: hidden; display: block;
            }
            .sb-canvas { transform-origin: 0 0; position: absolute; top: 0; left: 0; width: 100%; height: 100%; }
            .sb-svg-layer { position: absolute; top: 0; left: 0; pointer-events: none; overflow: visible; z-index: 0; }
            .sb-connector { fill: none; stroke: #94a3b8; stroke-width: 2px; }

            .sb-back-btn {
                position: absolute; top: 20px; left: 20px; 
                background: #fff; border: 1px solid #ddd; 
                width: 40px; height: 40px; border-radius: 8px; 
                cursor: pointer; display: flex; align-items: center; justify-content: center;
                z-index: 10000; box-shadow: 0 4px 12px rgba(0,0,0,0.1); 
                transition: all 0.2s; color: #333;
            }
            .sb-back-btn:hover { background: #fee2e2; color: #b91c1c; border-color: #fecaca; }

            .sb-controls {
                position: absolute; bottom: 20px; left: 20px;
                display: flex; flex-direction: column; 
                background: white; border-radius: 4px; overflow: hidden;
                box-shadow: 0 2px 10px rgba(0,0,0,0.15); z-index: 10000;
                border: 1px solid #e0e0e0;
            }
            .sb-ctrl-btn {
                width: 36px; height: 36px; border: none; background: white;
                display: flex; align-items: center; justify-content: center;
                cursor: pointer; border-bottom: 1px solid #f0f0f0; color: #555;
            }
            .sb-ctrl-btn:hover { background: #f9f9f9; color: #000; }
            .sb-ctrl-btn svg { width: 18px; height: 18px; }

            .sb-minimap {
                position: absolute; bottom: 20px; right: 20px;
                width: 200px; height: 150px; background: rgba(255,255,255,0.9);
                border: 1px solid #e0e0e0; border-radius: 6px;
                box-shadow: 0 4px 12px rgba(0,0,0,0.1); z-index: 10000;
                overflow: hidden; pointer-events: none;
            }
            .sb-mini-node { position: absolute; background: #cbd5e1; border-radius: 2px; }
            .sb-mini-viewport { position: absolute; border: 2px solid #48A9C5; background: rgba(72, 169, 197, 0.1); box-sizing: border-box; }           
            .sb-node {
                position: absolute; 
                width: ${this.cardWidth}px !important; 
                height: ${this.cardHeight}px !important;
                background: #fff; border: 1px solid #e5e7eb; border-radius: 12px;
                box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1); display: flex; flex-direction: column;
                transition: box-shadow 0.2s, transform 0.2s, border-color 0.2s; z-index: 1; overflow: hidden;
                box-sizing: border-box; user-select: none; 
            }
            .sb-node:hover {
                box-shadow: 0 20px 30px -5px rgba(0, 0, 0, 0.15); transform: translateY(-2px); z-index: 10; border-color: #F59E0B;
            }
            .sb-node.selected {
                border-color: #3b82f6 !important;
                box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.2), 0 20px 30px -5px rgba(0, 0, 0, 0.15);
                z-index: 20;
            }
            
            .sb-node-header {
                padding: 0 16px; display: flex; align-items: center; 
                justify-content: space-between; height: 50px; flex-shrink: 0;
            }
            .sb-title-input {
                background: transparent; border: 1px solid transparent;
                font-weight: 700; font-size: 16px; width: 100%; outline: none;
                border-radius: 4px; padding: 4px;
            }
            .sb-title-input:focus { background: white; color: #333 !important; }
            
            .sb-header-actions { display: flex; gap: 8px; margin-left: 10px; }
            .sb-act-btn { background: rgba(255,255,255,0.25); border: none; border-radius: 4px; cursor: pointer; padding: 6px; color: white; display:flex;}
            .sb-act-btn:hover { background: rgba(255,255,255,0.5); }

            .sb-node-body { 
                padding: 16px; font-size: 13px; color: #475569; display: flex; flex-direction: column; gap: 10px; flex: 1;
                overflow: hidden;
            }
            .sb-desc-input {
                width: 100%; flex: 1; resize: none; border: 1px solid transparent;
                font-family: inherit; font-size: 13px; color: #64748b; line-height: 1.5;
                background: transparent; border-radius: 4px; padding: 4px; box-sizing: border-box;
                user-select: text;
            }
            .sb-desc-input:hover { border-color: #e2e8f0; background: #f8fafc; }
            .sb-desc-input:focus { outline: none; border-color: #F59E0B; background: #fff; }

            .sb-stats { display: flex; align-items: center; justify-content: space-between; margin-top: auto; padding-top: 10px; border-top: 1px solid #f3f4f6; flex-shrink: 0;}
            .sb-badge { background: #f1f5f9; padding: 4px 10px; border-radius: 20px; font-size: 11px; font-weight: 600; color: #475569; display:flex; gap:5px; align-items:center; }
            
            .sb-profile { display: flex; gap: 12px; align-items: center; flex-shrink: 0; }
            .sb-avatar { width: 45px; height: 45px; border-radius: 50%; object-fit: cover; border: 3px solid #f8fafc; }
            .sb-p-info { flex: 1; overflow: hidden; }
            .sb-p-name { display: block; font-weight: 700; color: #1e293b; margin-bottom: 2px; font-size: 14px; }
            .sb-tags { display: flex; flex-wrap: wrap; gap: 4px; }
            .sb-tag { background: #e2e8f0; color: #475569; padding: 2px 8px; border-radius: 4px; font-size: 10px; font-weight: 600; }

            .sb-add-btn {
            position: absolute; width: 32px; height: 32px; 
            background: #fff; border: 1px solid #cbd5e1; border-radius: 50%; 
            display: flex; align-items: center; justify-content: center;
            cursor: pointer; box-shadow: 0 4px 6px rgba(0,0,0,0.05); color: #64748b;
            z-index: 5; transition: background 0.2s, transform 0.2s;
            transform: translateX(-50%); 
        }
        .sb-add-btn:hover { 
            background: var(--node-color, #F59E0B); 
            color: #fff; 
            border-color: var(--node-color, #d97706); 
            transform: translateX(-50%) scale(1.15); 
        }
        .sb-add-btn svg { width: 16px; height: 16px; }
            .sb-refresh-trigger { display: none; }
        `;
        const style = document.createElement('style');
        style.id = 'sb-styles-ui-v2';
        style.innerText = css;
        document.head.appendChild(style);
    }

    renderLayout() {
        const existing = document.querySelector('.sb-wrapper');
        if(existing) existing.remove();

        this.wrapper = document.createElement('div');
        this.wrapper.className = 'sb-wrapper';

        const backBtn = document.createElement('button');
        backBtn.className = 'sb-back-btn';
        backBtn.title = "Exit Sandbox";
        backBtn.innerHTML = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>`;
        backBtn.onclick = () => this.destroy();

        const controls = document.createElement('div');
        controls.className = 'sb-controls';
        controls.innerHTML = `
            <button class="sb-ctrl-btn" id="sb-zoom-in" title="Zoom In"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg></button>
            <button class="sb-ctrl-btn" id="sb-zoom-out" title="Zoom Out"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><line x1="5" y1="12" x2="19" y2="12"/></svg></button>
            <button class="sb-ctrl-btn" id="sb-fit" title="Fit View"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/></svg></button>
        `;

        this.minimap = document.createElement('div');
        this.minimap.className = 'sb-minimap';

        const refreshTrigger = document.createElement('button');
        refreshTrigger.className = 'sb-refresh-trigger';
        refreshTrigger.onclick = () => this.refreshData();

        this.canvas = document.createElement('div');
        this.canvas.className = 'sb-canvas';
        
        this.svgLayer = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        this.svgLayer.classList.add("sb-svg-layer");
        this.svgLayer.setAttribute("width", "50000"); 
        this.svgLayer.setAttribute("height", "50000");

        this.canvas.appendChild(this.svgLayer);
        this.wrapper.append(backBtn, controls, this.minimap, refreshTrigger, this.canvas);
        document.body.appendChild(this.wrapper);

        controls.querySelector('#sb-zoom-in').onclick = () => this.handleZoom(0.1);
        controls.querySelector('#sb-zoom-out').onclick = () => this.handleZoom(-0.1);
        controls.querySelector('#sb-fit').onclick = () => this.resetView();
    }

    calculateTreeLayout() {
        const nodeMap = {};
        this.data.forEach(n => {
            const sId = String(n.selfId);
            let pId = n.parentId ? String(n.parentId) : "0";
            if (pId === "null" || pId === "undefined" || pId === "") pId = "0";
            
            nodeMap[sId] = { ...n, selfId: sId, parentId: pId, children: [], treeWidth: 0, x: 0, y: 0 };
        });

        const allNodes = Object.values(nodeMap);
        if (allNodes.length === 0) return;

        let root = nodeMap[String(this.rootId)];
        if (!root) root = allNodes.find(n => n.parentId === "0" || n.parentId === "null");
        if (!root) root = allNodes[0];

        allNodes.forEach(n => {
            if (n.selfId === root.selfId) return;
            let pId = n.parentId;
            if (pId === "0" || !nodeMap[pId]) pId = root.selfId; 
            if (nodeMap[pId]) nodeMap[pId].children.push(n);
        });

        this.treeRoot = root;
        this.nodeLookup = nodeMap;

        this.measureTree(this.treeRoot);
        this.arrangeTree(this.treeRoot, 0, 0);
    }

    measureTree(node) {
        if (node.children.length === 0) {
            node.treeWidth = this.cardWidth + this.gapX;
            return node.treeWidth;
        }
        let w = 0;
        node.children.forEach(c => { w += this.measureTree(c); });
        node.treeWidth = w;
        return w;
    }

    arrangeTree(node, startX, level) {
        node.y = level * (this.cardHeight + this.gapY);
        
        if (node.children.length === 0) {
            node.x = startX + (this.gapX / 2);
            return;
        }

        let currentX = startX;
        node.children.forEach(c => {
            this.arrangeTree(c, currentX, level + 1);
            currentX += c.treeWidth;
        });

        const first = node.children[0];
        const last = node.children[node.children.length - 1];
        const firstCenter = first.x + (this.cardWidth / 2);
        const lastCenter = last.x + (this.cardWidth / 2);
        node.x = ((firstCenter + lastCenter) / 2) - (this.cardWidth / 2);
    }

    draw() {
        this.svgLayer.innerHTML = '';
        const oldNodes = this.canvas.querySelectorAll('.sb-node, .sb-add-btn');
        oldNodes.forEach(n => n.remove());
        if(this.treeRoot) this.drawRecursive(this.treeRoot);
        this.drawMinimap();
    }

    drawRecursive(node) {
        this.renderNodeCard(node);
        node.children.forEach(child => {
            this.drawLink(node, child);
            this.drawRecursive(child);
        });
    }


renderNodeCard(node) {
        const el = document.createElement('div');
        el.className = 'sb-node';
        if (node.selfId === this.selectedNodeId) el.classList.add('selected');
        
        el.dataset.id = node.selfId;
        el.style.left = `${node.x}px`;
        el.style.top = `${node.y}px`;

        el.addEventListener('mousedown', (e) => {
            if(e.target.closest('button') || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'INPUT') return;
            this.startNodeDrag(e, node);
        });

        el.addEventListener('click', (e) => { e.stopPropagation(); this.selectNode(node.selfId); });

        const svgTrash = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>`;
        const svgUser = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>`;

        // --- COUNT LOGIC ---
        const isRootNode = (node.selfId === this.rootId) || node.isRoot;
        
        // We use the count calculated in OrganiGram.js from the API data
        const displayCount = node.candidateCount || 0; 
        
        let summaryText = node.roleSummary || '';
        if(!summaryText && Array.isArray(node.description) && node.description.length > 0) {
             const lastEntry = [...node.description].reverse().find(d => d && typeof d.msg === 'string');
             if(lastEntry) summaryText = lastEntry.msg.replace('[ROLE_DESC_SAVE]','');
        }

        let badgeHTML = '';

        if (isRootNode) {
            // ROOT BADGE: Shows Total Headcount
            badgeHTML = `<div class="sb-badge filled" style="background:#FFFBEB; color:#B45309; border:1px solid #FCD34D;">${svgUser} <b>${displayCount} Total Headcount</b></div>`;
        } else {
            // ROLE BADGE: Shows Assigned count
            if (displayCount > 0) {
                badgeHTML = `<div class="sb-badge filled" style="background:#e0f2fe; color:#0284c7;">${svgUser} ${displayCount} Assigned</div>`;
            } else {
                badgeHTML = `<div class="sb-badge vacant">${svgUser} 0 Assigned</div>`;
            }
        }

        const headerColor = node.displaySettings?.cc || this.displaySettings?.cc || '#48A9C5';

        // Simplified Body (No Avatars in Sandbox, just Description)
        const bodyHTML = `<textarea class="sb-desc-input" placeholder="Role description...">${summaryText}</textarea>`;

        el.innerHTML = `
            <div class="sb-node-header" style="background-color: ${headerColor};">
                <input class="sb-title-input" value="${node.title}" />
                <div class="sb-header-actions">
                    <button class="sb-act-btn del-btn" title="Delete">${svgTrash}</button>
                </div>
            </div>
            <div class="sb-node-body">
                ${bodyHTML}
                <div class="sb-stats">
                    ${badgeHTML}
                </div>
            </div>
        `;

        // Input Bindings
        const titleInput = el.querySelector('.sb-title-input');
        if(titleInput) {
            titleInput.addEventListener('blur', (e) => {
                if(e.target.value !== node.title) this.actions.onUpdate && this.actions.onUpdate(node.selfId, { title: e.target.value });
            });
            titleInput.addEventListener('mousedown', (e) => e.stopPropagation());
        }
        const descInput = el.querySelector('.sb-desc-input');
        if(descInput) {
            descInput.addEventListener('blur', (e) => {
                if(e.target.value !== node.roleSummary) this.actions.onUpdate && this.actions.onUpdate(node.selfId, { roleSummary: e.target.value });
            });
            descInput.addEventListener('mousedown', (e) => e.stopPropagation());
        }

        el.querySelector('.del-btn').onclick = (e) => { e.stopPropagation(); this.actions.onDelete && this.actions.onDelete(node.selfId); };

        const addBtn = document.createElement('div');
        addBtn.className = 'sb-add-btn';
        addBtn.dataset.nodeId = node.selfId; 
        addBtn.innerHTML = `<span>+</span>`;
        addBtn.style.left = `${node.x + (this.cardWidth / 2)}px`;
        addBtn.style.top = `${node.y + this.cardHeight}px`;
        addBtn.style.setProperty('--node-color', headerColor);
        addBtn.addEventListener('mouseenter', () => { addBtn.style.background = headerColor; addBtn.style.borderColor = headerColor; });
        addBtn.addEventListener('mouseleave', () => { addBtn.style.background = '#fff'; addBtn.style.borderColor = '#cbd5e1'; });
        addBtn.onclick = (e) => { e.stopPropagation(); this.actions.onAdd && this.actions.onAdd(node.selfId); };

        this.canvas.appendChild(el);
        this.canvas.appendChild(addBtn);
    }

    drawLink(p, c) {
        const pX = p.x + (this.cardWidth / 2);
        const pY = p.y + this.cardHeight;
        const cX = c.x + (this.cardWidth / 2);
        const cY = c.y;
        const midY = pY + (cY - pY) / 2;
        const d = `M ${pX} ${pY} L ${pX} ${midY} L ${cX} ${midY} L ${cX} ${cY}`;
        const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
        path.setAttribute("d", d);
        path.setAttribute("class", "sb-connector");
        path.setAttribute("data-parent-id", p.selfId);
        path.setAttribute("data-child-id", c.selfId);
        this.svgLayer.appendChild(path);
    }

    drawMinimap() {
        if(!this.treeRoot || !this.minimap) return;
        this.minimap.innerHTML = '';
        
        let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
        const traverse = (n) => {
            if(n.x < minX) minX = n.x;
            if(n.x + this.cardWidth > maxX) maxX = n.x + this.cardWidth;
            if(n.y < minY) minY = n.y;
            if(n.y + this.cardHeight > maxY) maxY = n.y + this.cardHeight;
            n.children.forEach(traverse);
        };
        traverse(this.treeRoot);
        
        minX -= 300; maxX += 300; minY -= 300; maxY += 300;
        const treeW = maxX - minX;
        const treeH = maxY - minY;
        
        const mapW = 200;
        const mapH = 150;
        const scale = Math.min(mapW / treeW, mapH / treeH);
        
        const drawMini = (n) => {
            const d = document.createElement('div');
            d.className = 'sb-mini-node';
            d.style.left = `${(n.x - minX) * scale}px`;
            d.style.top = `${(n.y - minY) * scale}px`;
            d.style.width = `${this.cardWidth * scale}px`;
            d.style.height = `${this.cardHeight * scale}px`;
            this.minimap.appendChild(d);
            n.children.forEach(drawMini);
        };
        drawMini(this.treeRoot);
        
        this.miniViewport = document.createElement('div');
        this.miniViewport.className = 'sb-mini-viewport';
        this.minimap.appendChild(this.miniViewport);
        this.mapState = { minX, minY, scale };
        this.updateMinimapViewport();
    }

    updateMinimapViewport() {
        if(!this.miniViewport || !this.mapState) return;
        const screenW = window.innerWidth;
        const screenH = window.innerHeight;
        const worldLeft = -this.panX / this.zoom;
        const worldTop = -this.panY / this.zoom;
        const worldW = screenW / this.zoom;
        const worldH = screenH / this.zoom;
        const { minX, minY, scale } = this.mapState;
        
        this.miniViewport.style.left = `${(worldLeft - minX) * scale}px`;
        this.miniViewport.style.top = `${(worldTop - minY) * scale}px`;
        this.miniViewport.style.width = `${worldW * scale}px`;
        this.miniViewport.style.height = `${worldH * scale}px`;
    }

    handleZoom(delta) {
        this.zoom = Math.min(Math.max(0.2, this.zoom + delta), 2);
        this.updateTransform();
    }

    resetView() {
        if(!this.treeRoot) return;
        const wrapperW = window.innerWidth;
        this.zoom = 0.6;
        this.panX = (wrapperW / 2) - ((this.treeRoot.x + this.cardWidth/2) * this.zoom);
        this.panY = 80;
        this.updateTransform();
    }

    updateTransform() {
        this.canvas.style.transform = `translate(${this.panX}px, ${this.panY}px) scale(${this.zoom})`;
        this.updateMinimapViewport();
    }

    selectNode(id) {
        if (this.selectedNodeId) {
            const prev = this.canvas.querySelector(`.sb-node[data-id="${this.selectedNodeId}"]`);
            if (prev) prev.classList.remove('selected');
        }
        this.selectedNodeId = id;
        if (id) {
            const curr = this.canvas.querySelector(`.sb-node[data-id="${id}"]`);
            if (curr) curr.classList.add('selected');
        }
    }

    startNodeDrag(e, node) {
        e.stopPropagation();
        this.selectNode(node.selfId);
        
        this.isNodeDragging = true;
        this.dragNode = node;
        this.dragStart = { x: e.clientX, y: e.clientY };
        this.nodeStart = { x: node.x, y: node.y };
        this.originalPosBeforeMove[node.selfId] = { x: node.x, y: node.y };
        this.wrapper.style.cursor = 'grabbing';
    }

    moveSelectedNodeBy(dx, dy) {
        if(!this.selectedNodeId || !this.nodeLookup) return;
        const node = this.nodeLookup[this.selectedNodeId];
        if(!node) return;

        if (!this.originalPosBeforeMove[node.selfId]) {
            this.originalPosBeforeMove[node.selfId] = { x: node.x, y: node.y };
        }
        this.updateNodePosition(node, node.x + dx, node.y + dy);
    }

    cancelMove() {
        if (!this.selectedNodeId) return;
        this.isNodeDragging = false;
        this.dragNode = null;
        this.wrapper.style.cursor = 'default';

        const node = this.nodeLookup[this.selectedNodeId];
        const saved = this.originalPosBeforeMove[this.selectedNodeId];
        
        if (node && saved) {
            this.updateNodePosition(node, saved.x, saved.y);
            delete this.originalPosBeforeMove[this.selectedNodeId];
        }
        this.selectNode(null);
    }

    updateNodePosition(node, x, y) {
        node.x = x;
        node.y = y;

        const el = this.canvas.querySelector(`.sb-node[data-id="${node.selfId}"]`);
        if (el) {
            el.style.left = `${x}px`;
            el.style.top = `${y}px`;
        }

        const btn = this.canvas.querySelector(`.sb-add-btn[data-node-id="${node.selfId}"]`);
        if (btn) {
            btn.style.left = `${x + (this.cardWidth / 2)}px`;
            btn.style.top = `${y + this.cardHeight}px`;
        }

        this.updateLinksForNode(node);
    }

    updateLinksForNode(node) {
        const outgoing = this.svgLayer.querySelectorAll(`.sb-connector[data-parent-id="${node.selfId}"]`);
        outgoing.forEach(path => {
            const childId = path.dataset.childId;
            const childNode = this.nodeLookup[childId];
            if(childNode) this.redrawPath(path, node, childNode);
        });

        const incoming = this.svgLayer.querySelectorAll(`.sb-connector[data-child-id="${node.selfId}"]`);
        incoming.forEach(path => {
            const parentId = path.dataset.parentId;
            const parentNode = this.nodeLookup[parentId];
            if(parentNode) this.redrawPath(path, parentNode, node);
        });
    }

    redrawPath(pathEl, p, c) {
        const pX = p.x + (this.cardWidth / 2);
        const pY = p.y + this.cardHeight;
        const cX = c.x + (this.cardWidth / 2);
        const cY = c.y;
        const midY = pY + (cY - pY) / 2;
        const d = `M ${pX} ${pY} L ${pX} ${midY} L ${cX} ${midY} L ${cX} ${cY}`;
        pathEl.setAttribute("d", d);
    }

    attachEvents() {
        let startX=0, startY=0;
        this.wrapper.addEventListener('mousedown', (e) => {
            if(e.target.closest('.sb-node') || e.target.closest('button') || e.target.closest('.sb-add-btn') || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'INPUT') return;
            this.selectNode(null);
            this.isDragging = true;
            startX = e.clientX - this.panX;
            startY = e.clientY - this.panY;
            this.wrapper.style.cursor = 'grabbing';
        });
        
        window.addEventListener('mousemove', (e) => {
            if (this.isNodeDragging && this.dragNode) {
                e.preventDefault();
                const deltaX = (e.clientX - this.dragStart.x) / this.zoom;
                const deltaY = (e.clientY - this.dragStart.y) / this.zoom;
                this.updateNodePosition(this.dragNode, this.nodeStart.x + deltaX, this.nodeStart.y + deltaY);
                return;
            }
            if(this.isDragging) {
                e.preventDefault();
                this.panX = e.clientX - startX;
                this.panY = e.clientY - startY;
                this.updateTransform();
            }
        });
        
        window.addEventListener('mouseup', () => { 
            this.isDragging = false; 
            if (this.isNodeDragging) {
                this.isNodeDragging = false;
                this.dragNode = null;
                this.drawMinimap();
            }
            this.wrapper.style.cursor = 'default'; 
        });
        
        this.wrapper.addEventListener('wheel', (e) => {
            if (e.ctrlKey) { e.preventDefault(); this.handleZoom(e.deltaY > 0 ? -0.1 : 0.1); }
        });
    }
}